import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addToOrder, getPostPurchaseOffers } from '../api/lastupsell';
import {
  APIError,
  EditOrderAPIResponse,
  NetworkError,
} from '../shared/api/types';
import {
  CheckoutOrder,
  EditOrderOffer,
  LastUpsellSettings,
  LastUpsellText,
  LineItem,
  MoneyBag,
  MoneyFormats,
  Offer,
  PostPurchaseOffersResponseBody,
} from '../types';
import { logError, logInfo } from '../shared/utils/logging';
import captureException from '../utils/sentry';
import { useProductsContext } from './ProductsContext';
import { createContext } from '../dissolve/utils';
import { REGISTERED_APPS } from '../shared/types/Apps';
import { waitForShopifyCheckoutProperty } from '../shared/product-page-placement/currency';
import { getHandleForProductOrItemIdForService } from '../utils/offer-utils';
import { THANK_YOU_PAGE_TRANSLATIONS_KEY } from '../graphql/constants';
import getTranslationsMetaobject from '../graphql/utils/getTranslationsMetaobject';
import { getTranslatedOffersForCandyRack } from '../graphql/utils/getTranslatedOffers';

export interface UpsellContext {
  addOffer(offer: Offer): Promise<boolean>;

  removeOffer(offer: Offer): Promise<void>;

  acceptOffer(): Promise<EditOrderAPIResponse | APIError | NetworkError | null>;

  declineOffer(): Promise<void>;

  readonly offers?: Offer[];
  readonly settings?: LastUpsellSettings;
  readonly money_formats?: MoneyFormats;
  readonly currency?: string;
}

export interface OrderContext {
  readonly orderId?: number;
  readonly orderName?: string;
  readonly lineItems?: LineItem[];
  readonly subtotalPrice?: string;
  readonly totalPrice?: string;
  readonly totalPriceSet?: MoneyBag;
}

export interface PostPurchaseContext extends UpsellContext, OrderContext {}

export function isOrderStatusPage(): boolean {
  return !!window.Shopify.Checkout;
}

function normalizeLineItemPrices(lineItems: LineItem[] = []) {
  return lineItems.map((item) => {
    const newItem = { ...item };
    if (typeof item.line_price === 'number') {
      newItem.line_price = (item.line_price as any).toFixed(2);
    }
    if (typeof item.price === 'number') {
      newItem.price = (item.price as any).toFixed(2);
    }
    return newItem;
  });
}

export async function getOrderStatusCheckoutData(): Promise<
  OrderContext & {
    presentmentCurrency: string;
  }
> {
  if (!isOrderStatusPage()) throw Error('not in order status');

  return waitForShopifyCheckoutProperty().then(() => {
    const {
      order_id,
      line_items,
      subtotal_price,
      total_price,
      total_price_set,
      presentment_currency,
    } = window.Shopify.checkout as CheckoutOrder;

    let filteredLineItems = line_items.filter(
      (item) => item.product_id !== null && item.product_id !== undefined,
    );

    filteredLineItems = normalizeLineItemPrices(filteredLineItems);

    return {
      orderId: order_id,
      lineItems: filteredLineItems,
      subtotalPrice: subtotal_price,
      totalPrice: total_price,
      presentmentCurrency: presentment_currency,
      totalPriceSet: total_price_set,
    };
  });
}

const [usePostPurchaseContext, ContextProvider, ContextConsumer] =
  createContext<PostPurchaseContext>();

interface Props {
  children: React.ReactNode;
  orderName?: string;
  storefrontAccessToken: string;
}

const PostPurchaseContextProvider = ({
  children,
  orderName,
  storefrontAccessToken,
}: Props) => {
  const { removeOffersWithUnavailableProducts } = useProductsContext();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersState, setOffersState] =
    useState<PostPurchaseOffersResponseBody>();
  const [orderState, setOrderState] = useState<OrderContext>();

  useEffect(() => {
    async function loadOffers() {
      try {
        const {
          orderId,
          lineItems,
          subtotalPrice,
          totalPrice,
          presentmentCurrency,
          totalPriceSet,
        } = await getOrderStatusCheckoutData();
        setOrderState({
          orderId,
          lineItems,
          subtotalPrice,
          totalPrice,
          totalPriceSet,
        });

        if (lineItems && lineItems?.length !== 0 && orderId) {
          const response = await getPostPurchaseOffers(
            orderId,
            lineItems,
            totalPriceSet,
          );
          if (!response) {
            logInfo('was uninstalled.', REGISTERED_APPS.LAST_UPSELL);
            return;
          }

          const {
            offers: offersFromResponse,
            settings,
            currency,
            money_formats,
          } = {
            ...response,
          };

          let filteredOffers = offersFromResponse;
          if (offersFromResponse) {
            filteredOffers = await removeOffersWithUnavailableProducts(
              offersFromResponse,
            );
          }

          // Translating offer strings - returns offers in case of no translations
          const offersTranslated = await getTranslatedOffersForCandyRack(
            filteredOffers,
            storefrontAccessToken,
          );

          const translationsMetaobjectData = await getTranslationsMetaobject(
            storefrontAccessToken,
            THANK_YOU_PAGE_TRANSLATIONS_KEY,
          );

          // If translations are fetched, we use them, otherwise we default to fallback
          if (translationsMetaobjectData) {
            const {
              title,
              order_name,
              order_subheadline,
              add,
              added,
              back,
              continue_to,
              see_offers,
              subtitle,
            } = translationsMetaobjectData as unknown as LastUpsellText;

            // Setting translated strings, in case of empty string, we also display fallback
            const settingsWithUpdatedTranslations: LastUpsellSettings = {
              ...(settings as LastUpsellSettings),
              text: {
                title: title || settings.text.title,
                order_name: order_name || settings.text.order_name,
                order_subheadline:
                  order_subheadline || settings.text.order_subheadline,
                add: add || settings.text.add,
                added: added || settings.text.added,
                back: back || settings.text.back,
                continue_to: continue_to || settings.text.continue_to,
                see_offers: see_offers || settings.text.see_offers,
                subtitle: subtitle || settings.text.subtitle,
              },
            };

            setOffersState({
              offers: offersTranslated,
              settings: settingsWithUpdatedTranslations,
              currency: presentmentCurrency ? presentmentCurrency : currency,
              money_formats,
            });
          } else {
            setOffersState({
              offers: offersTranslated,
              settings,
              currency: presentmentCurrency ? presentmentCurrency : currency,
              money_formats,
            });
          }
        }
      } catch (error) {
        logError(
          'Error while fetching offers',
          REGISTERED_APPS.LAST_UPSELL,
          error,
        );
        captureException(
          error instanceof Error
            ? error
            : new Error('Error while fetching offers'),
        );
      }
    }

    loadOffers();
  }, [removeOffersWithUnavailableProducts, storefrontAccessToken]);

  const addOffer = async (offer: Offer) => {
    setOffers((_offers) => [..._offers, offer]);
    return true;
  };

  const removeOffer = async (offer: Offer, variantId?: number) => {
    setOffers((_offers) =>
      _offers.filter((_offer) => {
        return !(
          _offer.id === offer.id &&
          _offer.shopifyProductVariantIdComputedInFE === variantId
        );
      }),
    );
  };

  const acceptOffer = useCallback(async () => {
    if (!orderState) return null;
    const { orderId } = orderState;
    if (!orderId) return null;

    if (!offers.length) {
      return null;
    }
    const response = await addToOrder(
      orderId,
      offers.map((offer) => {
        let editOfferHandleData = getHandleForProductOrItemIdForService(offer);
        return {
          offer_id: offer.id,
          variant_id: offer.shopifyProductVariantIdComputedInFE,
          handle: editOfferHandleData,
          quantity: offer.quantityComputedInFE,
          note: offer.noteComputedInFE,
        } as EditOrderOffer;
      }),
    );
    return response;
  }, [orderState, offers]);

  const declineOffer = async () => Promise.resolve();

  const providerValue: PostPurchaseContext = useMemo(() => {
    let value: PostPurchaseContext = {
      addOffer,
      removeOffer,
      acceptOffer,
      declineOffer,
    };

    if (offersState) {
      value = {
        ...value,
        offers: offersState.offers,
        settings: offersState.settings,
        money_formats: offersState.money_formats,
        currency: offersState.currency,
      };
    }

    if (orderState) {
      value = {
        ...value,
        orderId: orderState.orderId,
        orderName,
        lineItems: orderState.lineItems,
        subtotalPrice: orderState.subtotalPrice,
        totalPrice: orderState.totalPrice,
      };
    }

    return value;
  }, [acceptOffer, offersState, orderState, orderName]);

  return <ContextProvider value={providerValue}>{children}</ContextProvider>;
};

const PostPurchaseContextConsumer =
  ContextConsumer as React.Consumer<PostPurchaseContext>;

export {
  usePostPurchaseContext,
  PostPurchaseContextProvider,
  PostPurchaseContextConsumer,
};
